import { QueryKeys } from "@/constants/Query";
import axiosInstance from "@/lib/axios";
import { useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import type * as fhirTypes from "fhir/r4";

export enum ProfessionalRole {
  LOCAL = 3, // Licensed Clinician (MD, NP, DO, etc)
  FOP_COACH = 6, // Clinical Associate (CA)
  MEDICAL_ASSISTANT = 7, // Medical Assistant (MA)
  ENGINEER = 8, // Engineer
  NURSE_PRACTITIONER = 9, // Nurse Practitioner (NP)
  SUPPORT_AGENT = 10, // Support Agent
}

export interface UserResponse {
  activeAffiliations: unknown[];
  activePartners: unknown[];
  created: string;
  email: string;
  inactive: boolean;
  isManagedAccount: boolean;
  mailingListOptedIn: boolean | null;
  membershipData: unknown | null;
  name: string;
  notifyPrimeLaunchOptedIn: boolean | null;
  patients: unknown[];
  preferredLanguage: string;
  professional: {
    displayNameExternal: string;
    displayNameInternal: string;
    fhir: fhirTypes.Practitioner;
    firstName: string;
    lastName: string;
    licenses: {
      expiresAt: string;
      medallionLicenseId?: string;
      medallionProviderId?: string;
      symplrLicenseId?: number;
      symplrProviderId?: number;
      state: string;
    }[];
    name: string;
    photo: string;
    role: ProfessionalRole;
    isQualifiedForPediatrics: boolean;
  };
  stripeCustomerId: string | null;
  timezone: string | null;
  userDependent: unknown | null;
  userId: number;
}

/**
 * Fetches the current user. Exported so non-hook callers (e.g. route
 * `beforeLoad` guards) can reuse the same request + cache key via
 * `queryClient.ensureQueryData`.
 */
export const fetchCurrentUser = async (): Promise<UserResponse> => {
  const response = await axiosInstance.get<UserResponse>("/user/me");

  return response.data;
};

export const useUser = ({ enabled } = { enabled: true }) => {
  return useQuery<UserResponse>({
    queryFn: fetchCurrentUser,
    queryKey: QueryKeys.User.ME,
    // Retry transient failures a few times so a single network blip doesn't
    // leave the whole session without a user, which breaks Sentry replay
    // attribution (we fall back to IP only). Skip 4xx — a 401/403 means the
    // clinician genuinely isn't authenticated, so retrying is pointless.
    retry: (failureCount, error) => {
      if (isAxiosError(error)) {
        const status = error.response?.status;
        if (status && status >= 400 && status < 500) return false;
      }
      return failureCount < 3;
    },
    enabled,
  });
};
